import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  type ConsentChoice,
  grantConsent,
  onConsentPromptRequested,
  readConsent,
} from '@/lib/analytics/consent'

/**
 * Analytics consent, asked once.
 *
 * Two rules drive the design, both legal rather than aesthetic:
 *
 *   1. **Refusing must be exactly as easy as accepting.** Same size, same
 *      position, same number of clicks. A quiet "manage preferences" link
 *      next to a bright "Accept all" is the pattern regulators fine people
 *      for, so both controls here are peers.
 *   2. **Nothing is dropped before the answer.** Consent Mode defaults to
 *      denied at boot (see `lib/analytics/consent.ts`), so this banner is
 *      not what stops a cookie being written — the default already did.
 *
 * Bottom-anchored and non-blocking on purpose. A modal over the page would
 * pressure the answer, and the page is readable either way.
 */
export function ConsentBanner() {
  const [choice, setChoice] = useState<ConsentChoice | null>(() => readConsent())

  // The footer's "Cookie settings" control reopens this. Withdrawal has to be
  // as easy as consenting, so there must always be a way back.
  useEffect(() => onConsentPromptRequested(() => setChoice(null)), [])

  if (choice !== null) return null

  function decide(next: ConsentChoice) {
    grantConsent(next)
    setChoice(next)
  }

  return (
    <div
      role="dialog"
      aria-label="Analytics consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-surface/95 backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-relaxed text-fg-muted">
          We&rsquo;d like to measure which pages people find useful. That
          needs a cookie, and only if you say yes.{' '}
          <a
            href="https://paxionfoundry.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-fg underline underline-offset-4 hover:text-primary"
          >
            How we handle data
          </a>
        </p>

        {/* Equal weight, equal size, side by side — see the note above. */}
        <div className="flex shrink-0 gap-3">
          <Button variant="outline" size="md" onClick={() => decide('denied')}>
            No thanks
          </Button>
          <Button variant="primary" size="md" onClick={() => decide('granted')}>
            Allow
          </Button>
        </div>
      </div>
    </div>
  )
}
